import { useCallback, useEffect } from 'react';

import type { TinyAIPageContext } from '@repo/types';

import { useAnalytics } from '../../../contexts/analytics/AnalyticsProvider';
import { useAutoResizeTextarea } from '../../../hooks/useAutoResizeTextarea';
import { useTinyAIPageContext } from '../../../hooks/useTinyAIPageContext';
import { useTinyAIContext } from '../TinyAIContext';

interface UseTinyAIChatComposerOptions {
  sendMessage: (
    content: string,
    isPredefinedPrompt?: boolean,
    promptId?: number,
    pageContextOverride?: TinyAIPageContext,
  ) => Promise<void>;
  isAnswering: boolean;
  isMobile: boolean;
  onSendStart?: () => void;
}

export const useTinyAIChatComposer = ({
  sendMessage,
  isAnswering,
  isMobile,
  onSendStart,
}: UseTinyAIChatComposerOptions) => {
  const { draftMessage: inputValue, setDraftMessage: setInputValue } =
    useTinyAIContext();
  const inputRef = useAutoResizeTextarea({ value: inputValue });

  const { trackEvent } = useAnalytics();
  const pageContext = useTinyAIPageContext();

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmed = inputValue.trim();
  const canSend = !!trimmed && !isAnswering;

  const handleSend = useCallback(async () => {
    if (!trimmed) return;
    if (isAnswering) return;

    setInputValue('');
    onSendStart?.();

    trackEvent('TinyAI message sent', {
      page_context: pageContext,
      source: 'chat',
    });

    await sendMessage(trimmed, false, undefined, pageContext);
  }, [
    trimmed,
    isAnswering,
    setInputValue,
    onSendStart,
    trackEvent,
    pageContext,
    sendMessage,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
        e.preventDefault();
        if (isAnswering) return;
        handleSend();
      }
    },
    [isMobile, isAnswering, handleSend],
  );

  return {
    inputValue,
    setInputValue,
    inputRef,
    handleSend,
    handleKeyDown,
    canSend,
  };
};
